import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import {
  getDrawerAuthorKey,
  getMetaText,
  getSectionWithSubGroups,
} from '../../PendingChangesDrawer.utils';
import PendingChangesCard from '../PendingChangesCard';

import {
  cardsContainerStyles,
  getExpandIconRotationStyles,
  getSubGroupExpandIconStyles,
  sectionGroupStyles,
  sectionHeaderLeftStyles,
  sectionHeaderStyles,
  sectionMetaStyles,
  sectionTitleStyles,
  subGroupCardsContainerStyles,
  subGroupHeaderLeftStyles,
  subGroupHeaderStyles,
  subGroupMetaStyles,
  subGroupTitleStyles,
} from './PendingChangesSectionGroup.styles';
import { getSubGroupMetaText } from './PendingChangesSectionGroup.utils';

const PendingChangesSectionGroup = ({
  sectionName,
  changes,
  changeCount,
  userCount,
  authorColorMap,
  onNavigate,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [collapsedSubGroups, setCollapsedSubGroups] = useState({});

  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const toggleSubGroup = useCallback(event => {
    event.stopPropagation();
    const { subgroup } = event.currentTarget.dataset;

    setCollapsedSubGroups(prev => ({ ...prev, [subgroup]: !prev[subgroup] }));
  }, []);

  const subGroups = useMemo(() => getSectionWithSubGroups(changes), [changes]);

  const hasSubGroups = useMemo(() => subGroups.some(sg => !_.isNil(sg.name)), [subGroups]);

  const expandIconRotationStyles = getExpandIconRotationStyles(isExpanded);
  const metaText = getMetaText(changeCount, userCount);

  const renderCards = cardsToRender =>
    cardsToRender.map(change => (
      <PendingChangesCard
        authorColor={authorColorMap[getDrawerAuthorKey(change)]}
        change={change}
        key={change.id}
        onNavigate={onNavigate}
      />
    ));

  return (
    <Box sx={sectionGroupStyles}>
      <Box sx={sectionHeaderStyles} onClick={toggleExpand}>
        <Box sx={sectionHeaderLeftStyles}>
          <Typography sx={sectionTitleStyles} variant="bold14">
            {sectionName}
          </Typography>
          <Typography sx={sectionMetaStyles} variant="regular12">
            — {metaText}
          </Typography>
        </Box>
        <ArrowCheckIcon
          direction={IconDirection.DOWN}
          size={IconSize[16]}
          style={expandIconRotationStyles}
        />
      </Box>

      {isExpanded && (
        <Box sx={cardsContainerStyles}>
          {hasSubGroups
            ? subGroups.map(subGroup => {
                const subGroupName = _.isNil(subGroup.name) ? 'Other' : subGroup.name;
                const isSubGroupCollapsed = collapsedSubGroups[subGroupName];
                const subGroupMeta = getSubGroupMetaText(subGroup.changes);

                return (
                  <Box key={subGroupName}>
                    <Box
                      data-subgroup={subGroupName}
                      sx={subGroupHeaderStyles}
                      onClick={toggleSubGroup}
                    >
                      <Box sx={subGroupHeaderLeftStyles}>
                        <ArrowCheckIcon
                          direction={IconDirection.DOWN}
                          size={IconSize[14]}
                          style={getSubGroupExpandIconStyles(!isSubGroupCollapsed)}
                        />
                        <Typography sx={subGroupTitleStyles} variant="medium14">
                          {subGroupName}
                        </Typography>
                      </Box>
                      <Typography sx={subGroupMetaStyles} variant="regular12">
                        {subGroupMeta}
                      </Typography>
                    </Box>
                    {!isSubGroupCollapsed && (
                      <Box sx={subGroupCardsContainerStyles}>{renderCards(subGroup.changes)}</Box>
                    )}
                  </Box>
                );
              })
            : renderCards(changes)}
        </Box>
      )}
    </Box>
  );
};

PendingChangesSectionGroup.propTypes = {
  sectionName: PropTypes.string.isRequired,
  changes: PropTypes.array.isRequired,
  changeCount: PropTypes.number.isRequired,
  userCount: PropTypes.number.isRequired,
  authorColorMap: PropTypes.object.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

export default memo(PendingChangesSectionGroup);
